
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface SubscriptionStatusResponse {
  success: boolean;
  active?: boolean;
  status?: string;
  end_date?: string | null;
  subscription_id?: string;
  token?: string | null;
  error?: string;
  message?: string;
}

export function useSubscriptionStatus() {
  const [loading, setLoading] = useState(false);
  const [response, setResponse] = useState<SubscriptionStatusResponse | null>(null);
  const { toast } = useToast();

  const checkSubscriptionStatus = async (productId: string, schoolId: string) => {
    setLoading(true);
    setResponse(null);
    
    try {
      if (!productId.trim() || !schoolId.trim()) {
        toast({
          variant: "destructive",
          title: "Error",
          description: "Product ID and School ID are required",
        });
        setLoading(false);
        return;
      }
      
      console.log(`Checking subscription status for product ${productId} and school ${schoolId}`);
      
      // Call the Supabase edge function
      const { data, error } = await supabase.functions.invoke('check-subscription-status', {
        body: {
          productId: productId.trim(),
          schoolId: schoolId.trim()
        }
      });
      
      if (error) {
        console.error('Subscription status check error:', error);
        
        setResponse({
          success: false,
          error: error.message || "Failed to check subscription status"
        });
        
        toast({
          variant: "destructive",
          title: "Error",
          description: error.message || "Failed to check subscription status",
        });
        
        setLoading(false);
        return;
      }
      
      // Handle successful response
      setResponse(data);
      
      if (!data.active) {
        toast({
          variant: "default",
          title: "Subscription Status",
          description: data.message || "No active subscription found",
        });
      } else {
        toast({
          title: "Subscription Status",
          description: `Subscription is ${data.status}. Expires: ${data.end_date ? new Date(data.end_date).toLocaleDateString() : 'N/A'}`,
        });
      }
    } catch (error: any) {
      console.error('Subscription status check error:', error);
      
      // Fixed the error by ensuring we're using the correct structure
      const errorResponse: SubscriptionStatusResponse = {
        success: false,
        error: error.message || "Failed to check subscription status"
      };
      
      setResponse(errorResponse);
      
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to check subscription status",
      });
    } finally {
      setLoading(false);
    }
  };

  return {
    loading,
    response,
    checkSubscriptionStatus
  };
}
